/**
 * Login Page Component
 * Handles user authentication with email and password
 * Supports optional 2FA verification and forced password change
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Form, Input, Button, Checkbox, Alert, Typography, Divider, Space } from 'antd';
import { UserOutlined, LockOutlined, LoginOutlined, SafetyOutlined, KeyOutlined } from '@ant-design/icons';
import { useAuth } from '../../context/AuthContext';
import { forcedPasswordChange } from '../../services/authService';
import PagesHeader from '../../PagesHeader';
import styles from './Login.module.css';

const { Title, Text } = Typography;

/**
 * Login page with email/password authentication
 * Supports optional 2FA verification step and forced password change
 * Redirects to intended destination after successful login
 */
const Login = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { login, verify2FA, loginWithRecoveryCode, isAuthenticated, loading, error, clearError } = useAuth();
  
  const [form] = Form.useForm();
  const [twoFactorForm] = Form.useForm();
  const [passwordChangeForm] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [loginError, setLoginError] = useState(null);
  
  // 2FA state
  const [requiresTwoFactor, setRequiresTwoFactor] = useState(false);
  const [twoFactorUserId, setTwoFactorUserId] = useState(null);
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);
  const [recoveryWarning, setRecoveryWarning] = useState(null);
  const [rememberMeForSession, setRememberMeForSession] = useState(false); // Track remember me through 2FA flow

  // Password change required state
  const [requiresPasswordChange, setRequiresPasswordChange] = useState(false);
  const [passwordChangeUserId, setPasswordChangeUserId] = useState(null);
  const [passwordChangeEmail, setPasswordChangeEmail] = useState(null);
  const [currentPasswordForChange, setCurrentPasswordForChange] = useState(null);

  // Get the redirect destination from location state or default to home
  const from = location.state?.from?.pathname || '/';

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);

  // Clear errors when component mounts or unmounts
  useEffect(() => {
    clearError();
    return () => clearError();
  }, [clearError]);

  // Sync context error with local state
  useEffect(() => {
    if (error) {
      setLoginError(error);
    }
  }, [error]);

  /**
   * Handle form submission
   */
  const handleSubmit = async (values) => {
    setSubmitting(true);
    setLoginError(null);

    try {
      // Pass rememberMe to extend session to 30 days
      const result = await login(values.email, values.password, values.remember);
      
      // Check if 2FA is required
      if (result.requiresTwoFactor) {
        setRequiresTwoFactor(true);
        setTwoFactorUserId(result.twoFactorUserId);
        setRememberMeForSession(values.remember); // Save for 2FA step
        // Remember email for later if checkbox is checked
        if (values.remember) {
          localStorage.setItem('rememberedEmail', values.email);
        } else {
          localStorage.removeItem('rememberedEmail');
        }
        return; // Wait for 2FA verification
      }

      // Check if password change is required (admin-enforced)
      if (result.passwordChangeRequired) {
        setRequiresPasswordChange(true);
        setPasswordChangeUserId(result.userId);
        setPasswordChangeEmail(result.email);
        setCurrentPasswordForChange(values.password);
        setRememberMeForSession(values.remember); // Save for password change step
        // Remember email for later if checkbox is checked
        if (values.remember) {
          localStorage.setItem('rememberedEmail', values.email);
        } else {
          localStorage.removeItem('rememberedEmail');
        }
        return; // Wait for password change
      }
      
      if (result.success) {
        // Remember email if checkbox is checked
        if (values.remember) {
          localStorage.setItem('rememberedEmail', values.email);
        } else {
          localStorage.removeItem('rememberedEmail');
        }
        
        // Navigate to intended destination
        navigate(from, { replace: true });
      } else {
        setLoginError(result.message || t('auth.loginFailed'));
      }
    } catch (err) {
      setLoginError(err.message || t('auth.loginFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Handle 2FA verification
   */
  const handleVerify2FA = async (values) => {
    setSubmitting(true);
    setLoginError(null);

    try {
      // Pass both rememberDevice (for 2FA bypass) and rememberMeForSession (for extended session)
      const result = await verify2FA(twoFactorUserId, values.verificationCode, values.rememberDevice, rememberMeForSession);
      
      if (result.success) {
        navigate(from, { replace: true });
      } else {
        setLoginError(result.message || t('auth.invalidVerificationCode'));
      }
    } catch (err) {
      setLoginError(err.message || t('auth.verificationFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Handle recovery code login
   */
  const handleRecoveryLogin = async (values) => {
    setSubmitting(true);
    setLoginError(null);
    setRecoveryWarning(null);

    try {
      // Pass rememberMeForSession for extended session
      const result = await loginWithRecoveryCode(twoFactorUserId, values.recoveryCode, rememberMeForSession);
      
      if (result.success) {
        // Show warning if running low on recovery codes
        if (result.warning) {
          setRecoveryWarning(result.warning);
        }
        navigate(from, { replace: true });
      } else {
        setLoginError(result.message || t('auth.invalidRecoveryCode'));
      }
    } catch (err) {
      setLoginError(err.message || t('auth.recoveryLoginFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Go back to email/password form
   */
  const handleBackToLogin = () => {
    setRequiresTwoFactor(false);
    setTwoFactorUserId(null);
    setUseRecoveryCode(false);
    setRequiresPasswordChange(false);
    setPasswordChangeUserId(null);
    setPasswordChangeEmail(null);
    setCurrentPasswordForChange(null);
    setRememberMeForSession(false); // Reset remember me state
    setLoginError(null);
    setRecoveryWarning(null);
    twoFactorForm.resetFields();
    passwordChangeForm.resetFields();
  };

  /**
   * Handle forced password change
   */
  const handleForcedPasswordChange = async (values) => {
    setSubmitting(true);
    setLoginError(null);

    try {
      const result = await forcedPasswordChange(
        passwordChangeUserId,
        currentPasswordForChange,
        values.newPassword,
        values.confirmPassword
      );
      
      if (result.success) {
        navigate(from, { replace: true });
      } else {
        setLoginError(result.message || t('auth.passwordChangeFailed'));
      }
    } catch (err) {
      setLoginError(err.message || t('auth.passwordChangeFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  // Get remembered email on mount
  useEffect(() => {
    const rememberedEmail = localStorage.getItem('rememberedEmail');
    if (rememberedEmail) {
      form.setFieldsValue({ email: rememberedEmail, remember: true });
    }
  }, [form]);

  return (
    <div className={styles.pageContainer}>
      <PagesHeader />
      
      <main className={styles.main}>
        <div className={styles.loginCard}>
          <div className={styles.header}>
            <Title level={2} className={styles.title}>
              {requiresPasswordChange 
                ? t('auth.passwordChangeRequired')
                : requiresTwoFactor 
                  ? t('auth.twoFactorVerification') 
                  : t('auth.welcomeBack')}
            </Title>
            <Text type="secondary" className={styles.subtitle}>
              {requiresPasswordChange
                ? t('auth.passwordChangeRequiredMessage')
                : requiresTwoFactor 
                  ? (useRecoveryCode ? t('auth.enterRecoveryCode') : t('auth.enterVerificationCode'))
                  : <>{t('auth.signInToContinue')} <span className={styles.brandAccent}>HeatCraftHP</span></>
              }
            </Text>
          </div>

          {loginError && (
            <Alert
              message={loginError}
              type="error"
              showIcon
              closable
              onClose={() => setLoginError(null)}
              className={styles.alert}
            />
          )}

          {recoveryWarning && (
            <Alert
              message={recoveryWarning}
              type="warning"
              showIcon
              closable
              onClose={() => setRecoveryWarning(null)}
              className={styles.alert}
            />
          )}

          {!requiresTwoFactor && !requiresPasswordChange && location.state?.from && (
            <Alert
              message={t('auth.loginRequired')}
              type="info"
              showIcon
              className={styles.alert}
            />
          )}

          {/* Password Change Required Form */}
          {requiresPasswordChange ? (
            <Form
              form={passwordChangeForm}
              name="passwordChange"
              onFinish={handleForcedPasswordChange}
              layout="vertical"
              requiredMark={false}
              className={styles.form}
            >
              <Alert
                message={t('auth.passwordChangeSecurityNotice')}
                type="warning"
                showIcon
                className={styles.alert}
                style={{ marginBottom: 24 }}
              />

              <Form.Item
                name="newPassword"
                label={t('auth.newPassword')}
                rules={[
                  { required: true, message: t('auth.newPasswordRequired') },
                  { min: 12, message: t('auth.passwordMinLength') },
                  {
                    pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).+$/,
                    message: t('auth.passwordComplexity')
                  }
                ]}
                extra={t('auth.passwordRequirements')}
              >
                <Input.Password
                  prefix={<LockOutlined className={styles.inputIcon} />}
                  placeholder={t('auth.newPasswordPlaceholder')}
                  size="large"
                  autoComplete="new-password"
                  disabled={submitting}
                />
              </Form.Item>

              <Form.Item
                name="confirmPassword"
                label={t('auth.confirmPassword')}
                dependencies={['newPassword']}
                rules={[
                  { required: true, message: t('auth.confirmPasswordRequired') },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('newPassword') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error(t('auth.passwordsMustMatch')));
                    },
                  }),
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined className={styles.inputIcon} />}
                  placeholder={t('auth.confirmPasswordPlaceholder')}
                  size="large"
                  autoComplete="new-password"
                  disabled={submitting}
                />
              </Form.Item>

              <Form.Item style={{ marginTop: 24 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  size="large"
                  block
                  loading={submitting || loading}
                  icon={<LockOutlined />}
                  className={styles.submitButton}
                >
                  {submitting ? t('auth.changingPassword') : t('auth.changePasswordAndLogin')}
                </Button>
              </Form.Item>

              <Space direction="vertical" style={{ width: '100%', textAlign: 'center' }}>
                <Button
                  type="link"
                  onClick={handleBackToLogin}
                  disabled={submitting}
                >
                  {t('auth.backToLogin')}
                </Button>
              </Space>
            </Form>
          ) : /* 2FA Verification Form */
          requiresTwoFactor ? (
            <Form
              form={twoFactorForm}
              name="twoFactor"
              onFinish={useRecoveryCode ? handleRecoveryLogin : handleVerify2FA}
              layout="vertical"
              requiredMark={false}
              className={styles.form}
            >
              {useRecoveryCode ? (
                // Recovery Code Input
                <Form.Item
                  name="recoveryCode"
                  label={t('auth.recoveryCode')}
                  rules={[
                    { required: true, message: t('auth.recoveryCodeRequired') },
                    { pattern: /^[A-Z0-9]{5}-[A-Z0-9]{5}$/i, message: t('auth.recoveryCodeFormat') }
                  ]}
                  extra={t('auth.recoveryCodeHint')}
                >
                  <Input
                    prefix={<KeyOutlined className={styles.inputIcon} />}
                    placeholder="XXXXX-XXXXX"
                    size="large"
                    autoComplete="off"
                    disabled={submitting}
                    style={{ fontFamily: 'monospace', letterSpacing: '0.1em' }}
                  />
                </Form.Item>
              ) : (
                // Authenticator Code Input
                <>
                  <Form.Item
                    name="verificationCode"
                    label={t('auth.verificationCode')}
                    rules={[
                      { required: true, message: t('auth.verificationCodeRequired') },
                      { pattern: /^\d{6}$/, message: t('auth.verificationCodeFormat') }
                    ]}
                    extra={t('auth.verificationCodeHint')}
                  >
                    <Input
                      prefix={<SafetyOutlined className={styles.inputIcon} />}
                      placeholder="000000"
                      size="large"
                      maxLength={6}
                      autoComplete="one-time-code"
                      disabled={submitting}
                      style={{ fontFamily: 'monospace', letterSpacing: '0.3em', textAlign: 'center' }}
                    />
                  </Form.Item>
                  
                  <Form.Item name="rememberDevice" valuePropName="checked" noStyle>
                    <Checkbox disabled={submitting}>
                      {t('auth.rememberDevice')}
                    </Checkbox>
                  </Form.Item>
                </>
              )}

              <Form.Item style={{ marginTop: 24 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  size="large"
                  block
                  loading={submitting || loading}
                  icon={<SafetyOutlined />}
                  className={styles.submitButton}
                >
                  {submitting ? t('auth.verifying') : t('auth.verify')}
                </Button>
              </Form.Item>

              <Space direction="vertical" style={{ width: '100%', textAlign: 'center' }}>
                <Button
                  type="link"
                  onClick={() => {
                    setUseRecoveryCode(!useRecoveryCode);
                    setLoginError(null);
                    twoFactorForm.resetFields();
                  }}
                  disabled={submitting}
                >
                  {useRecoveryCode ? t('auth.useAuthenticatorInstead') : t('auth.useRecoveryCode')}
                </Button>
                
                <Button
                  type="link"
                  onClick={handleBackToLogin}
                  disabled={submitting}
                >
                  {t('auth.backToLogin')}
                </Button>
              </Space>
            </Form>
          ) : (
            // Standard Email/Password Form
            <>
              <Form
                form={form}
                name="login"
                onFinish={handleSubmit}
                layout="vertical"
                requiredMark={false}
                className={styles.form}
              >
                <Form.Item
                  name="email"
                  label={t('auth.email')}
                  rules={[
                    { required: true, message: t('auth.emailRequired') },
                    { type: 'email', message: t('auth.emailInvalid') }
                  ]}
                >
                  <Input
                    prefix={<UserOutlined className={styles.inputIcon} />}
                    placeholder={t('auth.emailPlaceholder')}
                    size="large"
                    autoComplete="email"
                    disabled={submitting}
                  />
                </Form.Item>

                <Form.Item
                  name="password"
                  label={t('auth.password')}
                  rules={[
                    { required: true, message: t('auth.passwordRequired') }
                  ]}
                >
                  <Input.Password
                    prefix={<LockOutlined className={styles.inputIcon} />}
                    placeholder={t('auth.passwordPlaceholder')}
                    size="large"
                    autoComplete="current-password"
                    disabled={submitting}
                  />
                </Form.Item>

                <Form.Item className={styles.optionsRow}>
                  <div className={styles.formOptions}>
                    <Form.Item name="remember" valuePropName="checked" noStyle>
                      <Checkbox disabled={submitting}>
                        {t('auth.rememberMe')}
                      </Checkbox>
                    </Form.Item>
                    <Link to="/forgot-password" className={styles.forgotLink}>
                      {t('auth.forgotPassword')}
                    </Link>
                  </div>
                </Form.Item>

                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    size="large"
                    block
                    loading={submitting || loading}
                    icon={<LoginOutlined />}
                    className={styles.submitButton}
                  >
                    {submitting ? t('auth.signingIn') : t('auth.signIn')}
                  </Button>
                </Form.Item>
              </Form>

              <Divider plain className={styles.divider}>
                {t('auth.or')}
              </Divider>

              <div className={styles.registerPrompt}>
                <Text type="secondary">
                  {t('auth.noAccount')}{' '}
                </Text>
                <Link to="/register" className={styles.registerLink}>
                  {t('auth.createAccount')}
                </Link>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default Login;
